import { html, LitElement, PropertyValues, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import "../FloatingBubble/FloatingBubble";
import { FloatingBubbleComponent, Vector } from "../FloatingBubble/FloatingBubble";
import styles from "./BubbleModal.scss?inline";
import { _MOBILE_INSETS } from "../../../utils/Device";
import { IMobileInsets } from "../../../types/CustomEvents";

const BUBBLE_SIZE = 50; // px
const CLOSE_MODAL_DRAG_LIMIT = 5; // px
const MARGIN_TOP_FROM_PARENT = 30; // px
const MARGIN_FROM_PARENT = 15; // px
const MARGIN_BUBBLE_MODAL = 10; // px

@customElement("app-bubble-modal")
export class BubbleModalComponent extends LitElement {
    @property({ type: Boolean, reflect: true }) k_isOpen: boolean = false;
    @property({ type: Number, reflect: true }) k_bubbleSizePx: number = BUBBLE_SIZE;
    @property({ type: String, reflect: true }) k_bubbleBackgroundColor: string = "#007bff";
    @property({ type: String, reflect: false }) k_width: string = "200px";
    @property({ type: String, reflect: false }) k_height: string = "200px";
    @property({ type: String, reflect: false }) k_minWidth: string = null;
    @property({ type: String, reflect: false }) k_maxWidth: string = null;
    @property({ type: String, reflect: false }) k_minHeight: string = null;
    @property({ type: String, reflect: false }) k_maxHeight: string = null;
    @property({ type: String, reflect: false }) k_modalPadding: string = null;
    @property({ type: String, reflect: true }) k_storageKey: string | number = null;

    @query("app-floating-bubble") private _floatingBubble: FloatingBubbleComponent;
    @query(".bubble-modal") private _modal: HTMLElement;

    @state() private s_bubbleLadoX: "right" | "left" = "right";
    @state() private s_bubbleLadoY: "top" | "bottom" = "top";
    @state() private s_position: Vector = { x: 0, y: 0 };
    @state() private s_requireReOpenOnPositionChange: boolean = false;
    @state() private s_bottomDiff: number = 0;

    private _pointerDown: Vector | null = null;

    static styles = [unsafeCSS(styles)];

    render() {
        const size: number = this.getBubbleSizeFinal();
        const marginFromParent = this.getMarginFromParent();
        return html`
        <app-floating-bubble
            k_size=${size}
            k_backgroundColor=${this.k_bubbleBackgroundColor}
            k_marginTop=${marginFromParent.top}
            k_marginBottom=${marginFromParent.bottom}
            k_marginLeft=${marginFromParent.left}
            k_marginRight=${marginFromParent.right}
            .k_parentBounds=${this.parentElement || null}
            k_storageKey=${this.k_storageKey}
            @pointerdown=${this.onBubblePointerDown}
            @pointermove=${this.onBubblePointerMove}
            @bubblemodal-click=${this.toggleModal}
            @bubblemodal-position-change=${(e: CustomEvent<Vector>) => {
                this.updateSavedPosition(e.detail);
                this.calculateBubbleSide();
                this.calculateBottomDiff();
                if (this.s_requireReOpenOnPositionChange) {
                    this.s_requireReOpenOnPositionChange = false;
                    this.k_isOpen = true;
                }
            }}
        >
            <slot name="bubble-content"></slot>
        </app-floating-bubble>

        <div class="modal-container-ref">
            <div class=${`bubble-modal ${this.k_isOpen ? "open" : "closed"} ${this.s_bubbleLadoX} ${this.s_bubbleLadoY}`}
                style=${this.getStyleString()}
            >
                <div class="modal-header">
                    <slot name="modal-header"></slot>
                </div>
                <div class="modal-content">
                    <slot name="modal-content"></slot>
                </div>
                <div class="modal-footer">
                    <slot name="modal-footer"></slot>
                </div>
            </div>
        </div>
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        // inicializa posición de la burbuja
        this.updateSavedPosition();

        setTimeout(() => {
            if (this.k_isOpen) {
                this.updateSavedPosition();
                this.calculateBubbleSide();
                this.calculateBottomDiff();
                this.updateModalStylesOnly();
            }
        }, 50);

        let timeoutId: NodeJS.Timeout;
        window.addEventListener('resize', () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                this.updateSavedPosition();
                if (this.k_isOpen) {
                    this.updateModalStylesOnly(); // Forzar actualización de variables css
                }
            }, 100);
        });
    }

    private mobileinsetchangeListener = () => this.requestUpdate();

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener("mobileinsetchange", this.mobileinsetchangeListener);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener("mobileinsetchange", this.mobileinsetchangeListener);
    }

    protected update(changedProperties: PropertyValues): void {
        super.update(changedProperties);

        if (changedProperties.has("k_isOpen")) {
            this.updateSavedPosition();
            this.calculateBubbleSide();
        }
    }

    // **********************************************************************
    // #region Utils

    private toggleModal() {
        this.k_isOpen = !this.k_isOpen;
        this.calculateBubbleSide();
    }

    private getBubbleSizeFinal() {
        const bubbleSize = this.k_bubbleSizePx;
        return this.k_isOpen ? bubbleSize * 0.9 : bubbleSize;
    }

    private getModalStyles() {
        const bubbleSize = this.getBubbleSizeFinal();
        const { x, y } = this.s_position;
        const { left, right } = this.getMarginFromParent();
        return {
            "--width": this.k_width,
            "--height": this.k_height,
            "--minWidth": this.k_minWidth,
            "--maxWidth": this.k_maxWidth,
            "--minHeight": this.k_minHeight,
            "--maxHeight": this.k_maxHeight,
            "--modalPadding": this.k_modalPadding,
            "--bubbleSize": `${bubbleSize}px`,
            "--x": `${x || 0}px`,
            "--y": `${(y || 0) + this.s_bottomDiff}px`,
            "--marginFromParentLeft": `${left}px`,
            "--marginFromParentRight": `${right}px`,
            "--marginBubbleModal": `${MARGIN_BUBBLE_MODAL}px`,
        }
    }

    private getMarginFromParent(): IMobileInsets {
        const dimExtra = 14;
        const getDim = (dimention: number) => Math.max(dimention + dimExtra, MARGIN_FROM_PARENT);
        return {
            top: Math.max(getDim(_MOBILE_INSETS.top), MARGIN_TOP_FROM_PARENT),
            left: getDim(_MOBILE_INSETS.left),
            bottom: getDim(_MOBILE_INSETS.bottom),
            right: getDim(_MOBILE_INSETS.right),
        }
    }

    private getStyleString() {
        const styles = this.getModalStyles();
        return Object.entries(styles)
            .filter(([_, value]) => value !== null)
            .map(([key, value]) => `${key}: ${value};`).join(" ");
    }

    private updateModalStylesOnly() {
        const styles = this.getStyleString();
        this._modal.style.cssText = styles
    }

    /** retorna true si cambia de lado (izq, derecha, arriba, abajo) */
    private calculateBubbleSide(): boolean {
        const bubbleSize = this.getBubbleSizeFinal();
        const { x, y } = this._floatingBubble?._GetPosition() || { x: 0, y: 0 };
        const parentWidth = this.parentElement?.clientWidth || 0;
        const parentHeight = this.parentElement?.clientHeight || 0;

        const isOnRightSide = x + bubbleSize / 2 >= parentWidth / 2;
        const isOnTopSide = y + bubbleSize / 2 >= parentHeight / 2;
        const newLadoX = isOnRightSide ? "right" : "left";
        const newLadoY = isOnTopSide ? "top" : "bottom";

        const hasChanged = this.s_bubbleLadoX !== newLadoX || this.s_bubbleLadoY !== newLadoY;
        if (hasChanged) {
            this.s_bubbleLadoX = newLadoX;
            this.s_bubbleLadoY = newLadoY;
        }
        return hasChanged;
    }

    private calculateBottomDiff() {
        const modalRect = this._modal.getBoundingClientRect();
        const parentRect = document.body?.getBoundingClientRect() || { y: 0, height: 0, bottom: 0 };

        const bottomDiff = (this.s_position.y + modalRect.height) - (parentRect.y + parentRect.height);
        const marginFromParent = this.getMarginFromParent();
        this.s_bottomDiff = bottomDiff > 0 ? -(bottomDiff + marginFromParent.bottom) : 0;
    }

    private updateSavedPosition(position?: Vector) {
        this.s_position = position ? { ...position } : { ...this._floatingBubble._GetPosition() };
    }

    // Listener auxiliares para cancelar open si se intenta arrastrar mientras el modal está abierto

    private onBubblePointerDown(e: PointerEvent) {
        if (this.k_isOpen) {
            this._pointerDown = { x: e.clientX, y: e.clientY };
        } else {
            this._pointerDown = null;
        }
    }

    private onBubblePointerMove(e: PointerEvent) {
        if (this._pointerDown && this.k_isOpen) {
            // Valida si se intenta arrastrar mientras el modal está abierto para cerrarlo
            const pointerPosition: Vector = { x: e.clientX, y: e.clientY };
            const distance = Math.hypot(
                pointerPosition.x - this._pointerDown.x,
                pointerPosition.y - this._pointerDown.y,
            );
            this.updateSavedPosition();
            this.calculateBubbleSide();

            if (distance > CLOSE_MODAL_DRAG_LIMIT) {
                // console.log("Arrastre detectado, cerrando modal...");
                this._pointerDown = null;
                this.k_isOpen = false;
                this.s_requireReOpenOnPositionChange = true;
            }
        }
    }

    // #endregion
    // **********************************************************************
}